import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoad } from "@redotech/react-util/load";
import { useEffect } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";

interface Bucket {
  upperBound: string;
  price: string;
  currency: string;
  skuCount: number;
}

interface ForecastingCostBreakdownData {
  buckets: Bucket[];
  catchAllSkuCount: number;
  catchAllPrice: string;
  catchAllCurrency: string;
}

interface UseImsForecastingDataResult {
  data: ForecastingCostBreakdownData | undefined;
  isLoading: boolean;
  reload: () => void;
}

export function useImsForecastingData(
  teamId: string,
): UseImsForecastingDataResult {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);

  const [forecastingData, triggerLoad] = useTriggerLoad(async () => {
    const response = await rpcClient.getForecastingCostBreakdown({ teamId });

    // Data transforms after triggerload
    const allBuckets = response.buckets;
    const catchAllBucket = allBuckets.find((b) => b.upperBound === -1);
    const regularBuckets = allBuckets.filter((b) => b.upperBound > 0);

    const buckets = regularBuckets.map((b) => ({
      upperBound: b.upperBound.toString(),
      price: b.price.toString(),
      currency: b.currency,
      skuCount: b.skuCount,
    }));

    const transformedData: ForecastingCostBreakdownData = {
      buckets,
      catchAllSkuCount: catchAllBucket?.skuCount || 0,
      catchAllPrice: catchAllBucket?.price.toString() || "0",
      catchAllCurrency: catchAllBucket?.currency || "USD",
    };

    return transformedData;
  });

  useEffect(() => {
    triggerLoad();
  }, [triggerLoad]);

  return {
    data: forecastingData.value,
    isLoading: forecastingData.pending,
    reload: triggerLoad,
  };
}
